import clsx from "clsx"
import { useTranslation } from "react-i18next"
import { isAcceptedFile, openFilesWithPicker } from "@/utils/file"
import { useEffect, useMemo, useRef, useState } from "react"
import { Button } from "./ui/Button"
import { Icon } from "./icons/Icon"
import toast from "react-hot-toast"
import { useAtom, useAtomValue } from "jotai"
import {
	anchorPhotosAtom,
	currentPhotoAtom,
	isExpressSetupAtom,
	selectedPhotosAtom,
} from "@/store/photo/photos-state"
import { isCalibrationAtom, projectAtom } from "@/store/project/projects-state"

import { isAbortError } from "@/utils/error"
import { analytics } from "@/telemetry/amplitude"
import { Switch } from "./ui/Switch"
import { Spinner } from "./ui/Spinner"
import { Slider } from "./ui/Slider"
import stylePngUrl from "@assets/style.png?url"
import {
	focusedColorMatchImageURLAtom,
	colorMatchImageUrlsAtom,
	colorMatchImagesAtom,
	colorMatchImagesMapAtom,
	removeColorMatchImage,
	importColorMatchImages,
	currentColorMatchIndexAtom,
	retryColorMatchImageUpload,
} from "@/store/colorMatch/color-match-state"
import { colorMatchImageUploadStatusesAtom } from "@/store/colorMatch/color-match-upload-state"
import { store } from "@/store/store"
import { currentAdjustmentsAtom } from "@/store/store-history"
import { processImageUrl } from "@/utils/process-image-url"
import { queueApplyColorMatch } from "@/store/processing/image-processing"
import { hasShownOnboarding, markOnboardingShown } from "@/store/app-state"
import { pushDialog, replaceDialog } from "@/store/dialogs-state"
import { ConfirmDialog, ImageStyle } from "./Dialogs/ConfirmDialog"
import { SettingsDialog } from "./Dialogs/SettingsDialog"
import {
	applyColorMatchAdjustments,
	getColorMatchAdjustments,
	getSavedColorMatchAdjustments,
} from "@/store/colorMatch/color-match-processing"
import { getEditor } from "@/utils/useEditor"
import { isColorMatchDebugEnabled } from "@/utils/measurement/feature-flag"
import { leftExpandedPanelAtom } from "@/store/ui-views-state"
import { showContextMenu } from "./ContextMenuManager"
import { ContextMenuItem } from "./ui/ContextMenu"

export const ColorMatchPanel = () => {
	const { t } = useTranslation("components")
	const editor = getEditor()
	const currentPhoto = useAtomValue(currentPhotoAtom)
	const currentAdjustments = useAtomValue(currentAdjustmentsAtom)
	const project = useAtomValue(projectAtom)
	const selectedPhotos = useAtomValue(selectedPhotosAtom)
	const isCalibration = useAtomValue(isCalibrationAtom)
	const isExpressSetup = useAtomValue(isExpressSetupAtom)
	const suggestedPhotos = useAtomValue(anchorPhotosAtom)
	const { data: colorMatchImages, isInitialLoading: areColorMatchImagesLoading } =
		useAtomValue(colorMatchImagesAtom)

	const [index, setIndex] = useAtom(currentColorMatchIndexAtom)
	const [useParameters, setUseParameters] = useState(true)
	const [strength, setStrength] = useState(1)
	const [isDragging, setIsDragging] = useState(false)

	const currentImage = colorMatchImages[index]
	const currentImageUrl = useColorMatchImageURL({ imageId: currentImage?.id ?? "" })
	const colorMatchImageUploadStatuses = useAtomValue(colorMatchImageUploadStatusesAtom)
	const currentImageUploadStatus = currentImage
		? colorMatchImageUploadStatuses[currentImage.id]
		: undefined
	const isCurrentImageUploading = currentImageUploadStatus === "uploading"
	const currentImageNeedsUpload = Boolean(
		currentImage?.data && !currentImage.contentUrl && currentImageUploadStatus !== "synced",
	)

	const [isApplying, setIsApplying] = useState(false)
	const [isDeleting, setIsDeleting] = useState(false)

	const [leftExpandedPanel, setLeftExpandedPanel] = useAtom(leftExpandedPanelAtom)
	const previousImageCountRef = useRef<number | null>(null)
	const panelRef = useRef<HTMLDivElement>(null)

	const asShotWhiteBalance = editor.whiteBalance
	const skinToneOptimized = currentAdjustments.colorMatchSkinToneOptimized
	const isApplied = currentImage?.id == currentAdjustments.colorMatchImageId
	const hasSavedColorMatchAdjustments = () =>
		getSavedColorMatchAdjustments({
			photo: currentPhoto ?? undefined,
			referenceImage: currentImage,
			useParameters,
			skinToneOptimized,
		})

	// Switch to the reference image which has the same id as the currentAdjustments.colorMatchImageId
	useEffect(() => {
		if (!isApplying) {
			const foundIndex = colorMatchImages.findIndex(
				(image) => image.id === currentAdjustments.colorMatchImageId,
			)
			if (foundIndex !== -1) {
				setIndex(foundIndex)
			}
		}
	}, [currentAdjustments.colorMatchImageId])

	// Check if the current image has color match adjustments when the reference image is changed

	useEffect(() => {
		if (
			currentPhoto?.status === "none" &&
			(currentAdjustments.colorMatchImageId === currentImage?.id ||
				!currentAdjustments.colorMatchImageId)
		) {
			setIsApplying(false)
		}
	}, [currentPhoto?.status, currentAdjustments.colorMatchImageId, currentImage?.id])

	useEffect(() => {
		if (isApplied) {
			setStrength(currentAdjustments["colorMatchStrength"])
		} else {
			setStrength(1)
		}
	}, [isApplied, currentAdjustments.colorMatchStrength])

	useEffect(() => {
		if (areColorMatchImagesLoading) {
			return
		}

		const previousImageCount = previousImageCountRef.current
		const currentImageCount = colorMatchImages.length
		previousImageCountRef.current = currentImageCount

		if (
			previousImageCount !== null &&
			currentImageCount > previousImageCount &&
			leftExpandedPanel === "colormatch_samples"
		) {
			panelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
		}
	}, [areColorMatchImagesLoading, colorMatchImages.length, leftExpandedPanel])

	const applyColorMatch = async (strength: number, skinToneOptimized: boolean) => {
		if (!project || !currentImage || !currentPhoto || selectedPhotos.length === 0) {
			return
		}

		if (hasSavedColorMatchAdjustments() && selectedPhotos.length === 1) {
			await applyColorMatchAdjustments({
				photo: currentPhoto,
				referenceImage: currentImage,
				useParameters,
				strength,
				skinToneOptimized,
				asShotWhiteBalance,
				generatePreview: false,
			})
		} else {
			setIsApplying(true)
			selectedPhotos.forEach((photo) => {
				queueApplyColorMatch({
					photo,
					referenceImage: currentImage,
					useParameters,
					strength,
					skinToneOptimized,
					asShotWhiteBalance,
				})
			})
		}
		const appliedToSuggested = selectedPhotos.some((photo) => photo.isAnchorImage)

		//Also apply to suggested photos if it's during setup
		if (appliedToSuggested && isCalibration && !isExpressSetup) {
			const validPhotos = suggestedPhotos.filter((photo) => photo.kind == "default")
			if (validPhotos.length > 0) {
				validPhotos.forEach((photo) => {
					queueApplyColorMatch({
						photo,
						referenceImage: currentImage,
						useParameters,
						strength,
						skinToneOptimized,
						asShotWhiteBalance,
					})
				})
				showApplySuggestedOnboarding()
			}
		}
	}

	const previewColorMatch = async (strength: number, skinToneOptimized: boolean) => {
		if (!project || !currentImage || !currentPhoto || isApplying) {
			return
		}
		const shouldCompute = !hasSavedColorMatchAdjustments()

		const inputs = {
			photo: currentPhoto,
			referenceImage: currentImage,
			useParameters,
			strength,
			shouldCompute: shouldCompute,
			skinToneOptimized,
			asShotWhiteBalance,
		}

		if (shouldCompute) setIsApplying(true)

		const adjustments = await getColorMatchAdjustments(inputs)
		if (shouldCompute) setIsApplying(false)

		if (
			adjustments &&
			currentPhoto.id === inputs.photo.id &&
			currentImage.id === inputs.referenceImage.id &&
			strength === inputs.strength &&
			skinToneOptimized === inputs.skinToneOptimized
		) {
			editor.setAdjustments(adjustments)
		}
	}

	const showApplySuggestedOnboarding = () => {
		if (hasShownOnboarding("colorMatchAppliedToSuggestedShown")) {
			return
		}

		markOnboardingShown("colorMatchAppliedToSuggestedShown")
		pushDialog(ConfirmDialog, {
			imageSource: stylePngUrl,
			imageStyle: "fit" as ImageStyle,
			title: t("colorMatchPanel.appliedToSuggestedTitle"),
			message: t("colorMatchPanel.appliedToSuggestedMessage"),
			primary: {
				title: t("colorMatchPanel.continue"),
			},
			secondary: {
				title: t("colorMatchPanel.openSettings"),
				handler: () => {
					replaceDialog(SettingsDialog)
				},
			},
			trackingViewName: "onboarding_color_match_applied_suggested",
		})
	}

	const importImages = async (files: File[]) => {
		await importColorMatchImages(files)
	}

	const openSelectImageMenu = (e: React.MouseEvent) => {
		e.stopPropagation()

		showContextMenu(
			e,
			<div aria-label={t("colorMatchPanel.ariaColorMatchPresetMenu")}>
				<ContextMenuItem
					trackingLabel="import"
					aria-label={t("colorMatchPanel.ariaImportColorMatchImage")}
					onClick={() => void selectImage()}
				>
					{t("colorMatchPanel.importYourOwn")}
				</ContextMenuItem>
				<ContextMenuItem
					trackingLabel="browse"
					aria-label={t("colorMatchPanel.ariaBrowseColorMatchPresets")}
					onClick={() => setLeftExpandedPanel("colormatch_samples")}
				>
					{t("colorMatchPanel.browseSamples")}
				</ContextMenuItem>
			</div>,
		)
	}

	const selectImage = async () => {
		const pickerOptions: OpenFilePickerOptions = {
			types: [
				{
					description: "Image", // i18n-ignore — browser File Picker API
					accept: {
						"image/jpeg": [".jpg", ".jpeg", ".png", ".webp", ".avif", ".heic"],
					},
				},
			],
			excludeAcceptAllOption: true,
			multiple: true,
		}

		try {
			const files = await openFilesWithPicker(pickerOptions)
			if (files.length > 0) {
				void importImages(files)
			}
		} catch (e: unknown) {
			if (isAbortError(e)) {
				return
			}
			analytics.trackEvent({
				label: "select_source_image_error",
				type: "error",
				value: "file_picker",
			})
		}
	}

	const deleteImage = async () => {
		if (!currentImage) return
		const nextIndex = index + 1 < colorMatchImages.length ? index : 0
		setIsDeleting(true)
		await removeColorMatchImage(currentImage.id)
		setIsDeleting(false)
		setIndex(nextIndex)
	}

	const retryImageUpload = async () => {
		if (!currentImage || isCurrentImageUploading) {
			return
		}

		try {
			await retryColorMatchImageUpload(currentImage)
		} catch (e) {
			console.error("Retrying color match image upload", e)
		}
	}

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		setIsDragging(false)

		e.stopPropagation()
		e.preventDefault()

		const files = e.dataTransfer.files
		const validFiles: File[] = []
		try {
			for (const file of files) {
				if (isAcceptedFile(file.name, ["jpg", "jpeg", "png", "webp", "avif", "heic"])) {
					validFiles.push(file)
				}
			}

			if (validFiles.length === 0) {
				toast.error(t("colorMatchPanel.pleaseSelectJpegPngWebp"))
			} else {
				console.log("validFiles", validFiles)
				void importImages(validFiles)
			}

			analytics.trackEvent({
				label: "source_image_selected",
				type: "event",
				value: "drag_and_drop",
			})
		} catch (e: unknown) {
			console.error(e)
			analytics.trackEvent({
				label: "select_source_image_error",
				type: "error",
				value: "drag_and_drop",
			})
		}
	}

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault()
		setIsDragging(true)
		const item = e.dataTransfer.items[0]
		if (!item) {
			return
		}
	}

	const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault()
		setIsDragging(false)
	}

	const renderStrength = () => {
		return (
			<div className="flex flex-none flex-col bg-base-800 pt-2">
				<Slider
					className="ml-[-12px] aria-disabled:opacity-50"
					title={t("colorMatchPanel.sliderStrength")}
					panelName={t("colorMatchPanel.panelColorMatch")}
					focusKey="colorMatchStrength"
					defaultValue={1}
					value={strength}
					min={0}
					max={200}
					step={1}
					plusMinusStep={5}
					shiftPlusMinusStep={20}
					onUpdate={(val) => {
						setStrength(val)
						void previewColorMatch(val, skinToneOptimized)
					}}
					onUpdateEnded={async (val) => {
						await previewColorMatch(val, skinToneOptimized)
						applyColorMatch(val, skinToneOptimized)
					}}
					disabled={!currentImage || !isApplied}
				/>
			</div>
		)
	}

	return (
		<div ref={panelRef}>
			{isColorMatchDebugEnabled() && (
				<div className="mb-2 flex flex-row items-center space-x-2 text-xs">
					{t("colorMatchPanel.outputParameters")}
					<div className="flex-1"></div>
					<Switch
						trackingLabel="output_parameters"
						aria-label={t("colorMatchPanel.ariaToggleColorMatchOutputParameters")}
						checked={useParameters}
						onCheckedChange={(checked) => {
							setUseParameters(checked)
						}}
					/>
				</div>
			)}
			{isColorMatchDebugEnabled() && (
				<div className="mt-1 flex flex-row items-center space-x-2 text-xs">
					{t("colorMatchPanel.optimizeForSkinTone")}
					<div className="flex-1"></div>
					<Switch
						trackingLabel="optimize_skin_tone"
						aria-label={t("colorMatchPanel.ariaToggleColorMatchSkinToneOptimization")}
						checked={skinToneOptimized}
						onCheckedChange={(checked) => {
							editor.setAdjustments({ colorMatchSkinToneOptimized: checked })
							applyColorMatch(strength, checked)
						}}
						//disabled={!currentImage || !isApplied}
					/>
				</div>
			)}
			<div
				className="h-32 w-full pt-2"
				data-allow-file-drop
				onDragOver={handleDragOver}
				onDrop={handleDrop}
				onDragLeave={handleDragLeave}
			>
				{currentImage && (
					//Image carousel
					<div className="relative size-full items-end">
						{currentImageUrl && (
							<img
								src={currentImageUrl}
								className={clsx(
									"absolute z-0 size-full rounded-md bg-base-700 object-contain",
								)}
								crossOrigin="anonymous"
							/>
						)}
						<div className="absolute z-10 flex size-full flex-col items-center justify-between">
							<div className="flex w-full flex-row items-center justify-between p-1">
								<Button
									trackingLabel="delete_image"
									aria-label={t("colorMatchPanel.ariaDeleteColorMatchImage")}
									variant="icon"
									size="custom"
									onClick={deleteImage}
									disabled={isDeleting}
								>
									{!isDeleting && <Icon name="delete" size={14} />}
									{isDeleting && <Spinner size={14} />}
								</Button>

								<Button
									trackingLabel="add_image"
									aria-label={t("colorMatchPanel.ariaAddColorMatchImage")}
									variant="icon"
									size="custom"
									onClick={(e) => openSelectImageMenu(e)}
								>
									<Icon name="add" size={14} />
								</Button>
							</div>

							{(isCurrentImageUploading || currentImageNeedsUpload) && (
								<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
									{isCurrentImageUploading && (
										<div className="flex size-9 items-center justify-center rounded-md bg-base-800/80">
											<Spinner size={16} />
										</div>
									)}
									{!isCurrentImageUploading && currentImageNeedsUpload && (
										<Button
											trackingLabel="retry_image_upload"
											aria-label={t(
												"colorMatchPanel.retryColorMatchImageUpload",
											)}
											tooltip={t("colorMatchPanel.retryUpload")}
											variant="icon"
											size="custom"
											className="pointer-events-auto"
											onClick={() => void retryImageUpload()}
										>
											<Icon name="refresh" size={14} />
										</Button>
									)}
								</div>
							)}

							<div className="flex w-full flex-row items-center justify-between p-1">
								<Button
									trackingLabel="previous_image"
									aria-label={t("colorMatchPanel.ariaPreviousColorMatchImage")}
									variant="icon"
									size="custom"
									onClick={() =>
										setIndex(
											(index - 1 + colorMatchImages.length) %
												colorMatchImages.length,
										)
									}
								>
									<Icon name="left" size={14} />
								</Button>
								<div className="bg-base-800/50 px-2 py-1 text-xs">
									{index + 1} / {colorMatchImages.length}
								</div>
								<Button
									trackingLabel="next_image"
									aria-label={t("colorMatchPanel.ariaNextColorMatchImage")}
									variant="icon"
									size="custom"
									onClick={() => setIndex((index + 1) % colorMatchImages.length)}
								>
									<Icon name="right" size={14} />
								</Button>
							</div>
						</div>
					</div>
				)}
				{!currentImage && (
					//Drag and drop area for empty state
					<div
						className={clsx(
							isDragging ? "bg-base-600" : "bg-base-700",
							"flex size-full flex-col items-center justify-center space-y-2 rounded-md border border-dashed border-base-400 hover:bg-base-400/30",
						)}
						onClick={(e) => openSelectImageMenu(e)}
					>
						<Button
							trackingLabel="select_image"
							aria-label={t("colorMatchPanel.ariaSelectColorMatchImage")}
							variant="custom"
							size="custom"
						>
							<Icon name="add" className="hover:text-base-400" />
						</Button>

						<div className="text-xs">{t("colorMatchPanel.chooseSourceImage")}</div>
					</div>
				)}
			</div>
			{renderStrength()}
			<Button
				trackingLabel="apply_color_match"
				aria-label={t("colorMatchPanel.ariaApplyCurrentColorMatchImage")}
				className="relative inline-flex w-full flex-1"
				size="sm"
				variant="default"
				onClick={() => applyColorMatch(strength, skinToneOptimized)}
			>
				<div className={clsx(isApplying && "invisible")}>
					{t("colorMatchPanel.applyCurrentImage")}
				</div>
				{isApplying && <Spinner className="absolute" size={14} />}
			</Button>
		</div>
	)
}

const useColorMatchImageURL = ({ imageId }: { imageId: string }) => {
	const focusedAtom = useMemo(() => focusedColorMatchImageURLAtom(imageId ?? ""), [imageId])
	const imageUrl = useAtomValue(focusedAtom)
	const imagesMap = useAtomValue(colorMatchImagesMapAtom)
	const imageItem = imagesMap[imageId]

	useEffect(() => {
		if (!imageUrl && imageItem) {
			if (imageItem.data) {
				const url = URL.createObjectURL(imageItem.data)
				store.set(colorMatchImageUrlsAtom, {
					...store.get(colorMatchImageUrlsAtom),
					[imageId]: url,
				})
			} else if (imageItem.contentUrl) {
				//Use remote url
				store.set(colorMatchImageUrlsAtom, {
					...store.get(colorMatchImageUrlsAtom),
					[imageId]: processImageUrl({
						url: imageItem.contentUrl,
						shortestSidePxSizeOfResize: 500,
					}),
				})
			}
		}
	}, [imageId, imageUrl, imageItem])

	return imageUrl
}
